/**
 * Faction indicator — compact icons in HUD sub-row
 * Tap to open faction status popup with all 3 factions' details
 */

import { FACTION_DEFS, FAVORABILITY_TIERS } from '../game/data/factions.js';
import type { GameState, FactionId } from '../game/index.js';
import { t } from '../i18n/index.js';

type GetState = () => GameState;

let containerEl: HTMLElement | null = null;
let getStateRef: GetState | null = null;
let popupEl: HTMLDivElement | null = null;

export function initFactionIndicator(
  container: HTMLElement,
  getState: GetState,
): { update: () => void } {
  getStateRef = getState;

  const wrapper = document.createElement('div');
  wrapper.className = 'faction-hud-icons';
  wrapper.id = 'faction-indicator';

  for (const def of FACTION_DEFS) {
    const dot = document.createElement('div');
    dot.className = 'faction-hud-dot';
    dot.dataset.factionId = def.id;
    dot.style.cssText = `background:${def.color};border:1px solid ${def.color}80`;
    dot.innerHTML = `<span class="faction-hud-tier" data-tier-label="${def.id}">1</span>`;
    wrapper.appendChild(dot);
  }

  wrapper.addEventListener('click', (e) => {
    e.stopPropagation();
    togglePopup();
  });

  container.appendChild(wrapper);
  containerEl = wrapper;

  document.addEventListener('click', () => {
    if (popupEl) closePopup();
  });

  return { update };
}

function togglePopup(): void {
  if (popupEl) { closePopup(); return; }
  if (!getStateRef || !containerEl) return;

  const state = getStateRef();
  popupEl = document.createElement('div');
  popupEl.className = 'faction-popup-overlay';
  popupEl.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).classList.contains('faction-popup-overlay')) {
      closePopup();
    }
  });

  let cardsHtml = '';
  for (const def of FACTION_DEFS) {
    const rel = state.faction.relations[def.id as FactionId];
    const maxTier = FAVORABILITY_TIERS.length;
    const nextThreshold = rel.tier < maxTier ? FAVORABILITY_TIERS[rel.tier] : FAVORABILITY_TIERS[maxTier - 1];
    const prevThreshold = rel.tier > 1 ? FAVORABILITY_TIERS[rel.tier - 1] : 0;
    const progressPct = rel.tier >= maxTier ? 100
      : Math.min(100, ((rel.favorability - prevThreshold) / (nextThreshold - prevThreshold)) * 100);

    const bonusLines = def.bonuses.map(b => {
      const active = rel.tier >= b.tierRequired;
      return `<div class="faction-popup-bonus ${active ? 'active' : ''}">
        <span>${active ? '\u2705' : `T${b.tierRequired}`}</span>
        <span>${t(b.descKey)}</span>
      </div>`;
    }).join('');

    cardsHtml += `
      <div class="faction-popup-card" style="border-left:3px solid ${def.color}">
        <div class="faction-popup-name" style="color:${def.color}">${t(def.nameKey)}</div>
        <div class="faction-popup-bar-wrap">
          <div class="faction-popup-bar" style="width:${progressPct}%;background:${def.color}"></div>
        </div>
        <div class="faction-popup-stats">
          <span>${t('faction_favorability')}: ${rel.favorability}</span>
          <span>${t('faction_tier').replace('{n}', String(rel.tier))}</span>
        </div>
        <div class="faction-popup-bonuses">${bonusLines}</div>
      </div>
    `;
  }

  popupEl.innerHTML = `
    <div class="faction-popup-content" onclick="event.stopPropagation()">
      <div class="faction-popup-title">${t('faction_status_title')}</div>
      ${cardsHtml}
      <button class="faction-popup-close" id="faction-popup-close">${t('close')}</button>
    </div>
  `;

  document.body.appendChild(popupEl);

  document.getElementById('faction-popup-close')?.addEventListener('click', () => {
    closePopup();
  });
}

function closePopup(): void {
  if (popupEl) { popupEl.remove(); popupEl = null; }
}

function update(): void {
  if (!containerEl || !getStateRef) return;
  const state = getStateRef();

  for (const def of FACTION_DEFS) {
    const rel = state.faction.relations[def.id as FactionId];
    const tierLabel = containerEl.querySelector(`[data-tier-label="${def.id}"]`);
    if (tierLabel) tierLabel.textContent = String(rel.tier);
  }
}
